'use client';
import Image from 'next/image';
import Link from 'next/link';
import { noto_sans, ubuntu } from '@/globalConstants';

type InlineLinkParagraph = {
    before: string;
    linkText: string;
    href: string;
    after: string;
};

export type GuidanceSection = {
    key: string;
    title: string;
    icon: string;
    intro?: string;
    subIntro?: string;
    bullets?: string[];
    outro?: InlineLinkParagraph;
    paragraphs?: (string | InlineLinkParagraph)[];
};

const GUIDANCE_SECTIONS: GuidanceSection[] = [
    {
        key: 'festival-pooja',
        title: 'Festival Pooja & Pandit Booking',
        icon: '/guidance/jyotishi.webp',
        intro: 'Want a Pandit-led pooja for this festival at home, or online if you live abroad? Dharmayana connects you with verified Pandits for all major Hindu festivals — Diwali, Navratri, Shivratri, Janmashtami, Ganesh Chaturthi and more.',
        subIntro: 'You can book:',
        bullets: [
            'Festival pooja at home with a verified Pandit',
            'Online / virtual pooja with live video and digital prasad',
            'Same-day sankalp for time-sensitive festivals',
        ],
        outro: {
            before: 'Bookings, samagri lists and Pandit assignment are all handled inside the ',
            linkText: 'Dharmayana app',
            href: '/apps',
            after: ' — choose the festival, pick a date and the rest is taken care of.',
        },
    },
    {
        key: 'prarthana',
        title: 'Prarthana, Mantras & Vrat Katha',
        icon: '/guidance/panchanga.webp',
        intro: 'Every festival has its own prarthanas, stotras and vrat kathas. Reading or listening to them on the festival day is the simplest way to observe the tradition at home.',
        subIntro: 'Inside the Dharmayana app you can listen to:',
        bullets: [
            '1000+ prarthanas, mantras and stotras with clear audio',
            'Festival-specific kathas — Lakshmi, Durga, Shiva, Ganesha, Krishna',
            'Multilingual versions: Sanskrit, Hindi, Tamil, Telugu, Kannada and more',
        ],
        outro: {
            before: 'Browse the full collection of ',
            linkText: 'prarthanas',
            href: '/prarthana',
            after: ' or get the app to play them on the festival morning without ads.',
        },
    },
    {
        key: 'panchanga',
        title: 'Festival Date, Tithi & Muhurat',
        icon: '/guidance/kundali.webp',
        paragraphs: [
            'Hindu festivals are tied to a specific Tithi, not a fixed date — so the day shifts every year and even varies slightly across cities.',
            {
                before: 'Open the ',
                linkText: 'Panchanga for the festival day',
                href: '/panchanga',
                after: ' to see the exact Tithi, Nakshatra, Yoga, Karana and Shubh Muhurat for pooja in your location.',
            },
            'The Dharmayana app keeps track of all upcoming festivals on the Hindu calendar and reminds you in advance.',
        ],
    },
    {
        key: 'astrology',
        title: 'Personalised Astrology Around Festivals',
        icon: '/guidance/matching.webp',
        intro: 'Festivals are also a good time to look at your kundali — many families plan sankalp, donations or new beginnings around them.',
        subIntro: 'Inside the app you can:',
        bullets: [
            'Generate your personal Vedic kundali',
            'Talk to a Jyotisha for guidance around career, relationships or health',
            'Run kundali matching before fixing a marriage during the festival season',
        ],
        outro: {
            before: 'All of this is available inside the ',
            linkText: 'Dharmayana app',
            href: '/apps',
            after: ' — no fixed schedule, use it whenever you need it.',
        },
    },
];

const FestivalAppCTA = ({ festivalName, heading, sections = GUIDANCE_SECTIONS }: { festivalName?: string; heading?: string; sections?: GuidanceSection[] }) => {
    return (
        <section className="w-full bg-white rounded-[8px] border-[1px] border-[#E5E0DF] p-4 md:p-6 shadow-sm flex flex-col gap-6">
            {heading && (
                <h2 className={`${ubuntu.className} font-[600] text-[22px] md:text-[24px] text-[#1D4749]`}>
                    {heading}
                </h2>
            )}

            {sections.map((section, idx) => (
                <div
                    key={section.key}
                    className={`flex flex-col gap-3 ${idx !== 0 ? 'border-t border-[#EDE4DD] pt-6' : 'border-t border-[#EDE4DD] pt-6'}`}
                >
                    <div className="flex items-center gap-3">
                        <Image
                            src={section.icon}
                            alt=""
                            width={36}
                            height={36}
                            className="w-9 h-9 shrink-0"
                        />
                        <h3 className={`${ubuntu.className} font-[500] text-[20px] md:text-[22px] text-[#1D4749]`}>
                            {section.title}
                        </h3>
                    </div>
                    {section.intro && (
                        <p className={`${noto_sans.className} text-[15px] md:text-[16px] leading-[1.6] text-[#8F8B8B]`}>
                            {section.intro}
                        </p>
                    )}
                    {section.subIntro && (
                        <p className={`${noto_sans.className} text-[15px] md:text-[16px] leading-[1.6] text-[#3C3838] font-[500]`}>
                            {section.subIntro}
                        </p>
                    )}
                    {section.bullets && (
                        <ul className={`${noto_sans.className} text-[15px] md:text-[16px] leading-[1.7] text-[#8F8B8B] list-disc pl-6 space-y-1`}>
                            {section.bullets.map((bullet, i) => (
                                <li key={i}>{bullet}</li>
                            ))}
                        </ul>
                    )}
                    {section.outro && (
                        <p className={`${noto_sans.className} text-[15px] md:text-[16px] leading-[1.6] text-[#8F8B8B]`}>
                            {section.outro.before}
                            <Link
                                href={section.outro.href}
                                className="text-[#EE9322] font-[500] hover:underline no-underline"
                            >
                                {section.outro.linkText}
                            </Link>
                            {section.outro.after}
                        </p>
                    )}
                    {section.paragraphs?.map((paragraph, i) => (
                        <p
                            key={i}
                            className={`${noto_sans.className} text-[15px] md:text-[16px] leading-[1.6] text-[#8F8B8B]`}
                        >
                            {typeof paragraph === 'string' ? (
                                paragraph
                            ) : (
                                <>
                                    {paragraph.before}
                                    <Link
                                        href={paragraph.href}
                                        className="text-[#EE9322] font-[500] hover:underline no-underline"
                                    >
                                        {paragraph.linkText}
                                    </Link>
                                    {paragraph.after}
                                </>
                            )}
                        </p>
                    ))}
                </div>
            ))}
        </section>
    );
};

export default FestivalAppCTA;
